import './index.less';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ApplicationStatusEnum } from '../../../enum/applicationStepStatus';
import { numberWithCommas } from '../../../helpers/number';
import { getApplicationDetailsThunk } from '../../../services/applicationService/applicationThunk';
import { AppDispatch, RootState } from '../../../store/store';
import { displayDateFromString } from '../../../utils/datetime.util';
import { getNricInfo } from '../../../utils/nric.util';
import { trimPhoneNo } from '../../../utils/number.util';
import CustomerFormModal from '../../CustomerFormModal';
import InfoGrid from '../../InfoGrid';

dayjs.extend(customParseFormat, {
  parseTwoDigitYear: (yearString: string) =>
    +yearString + (+yearString > +dayjs().format('YY') ? 1900 : 2000),
});

interface ApplicationInfoTabProps {
  applicationData?: any;
  readOnlyMode?: boolean;
}

const ApplicationInfoTab = ({ applicationData, readOnlyMode = false }: ApplicationInfoTabProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const readOnly = queryParams.get('readOnly') === 'true';

  const { applicationDetails } = useSelector((state: RootState) => state.application);

  // Use props if available, otherwise fall back to Redux state
  const currentApplicationData = applicationData || applicationDetails?.data;

  const editable =
    currentApplicationData?.applicationStatus === ApplicationStatusEnum.PENDING ||
    currentApplicationData?.applicationStatus === ApplicationStatusEnum.REFERBACK ||
    currentApplicationData?.applicationStatus ===
      ApplicationStatusEnum.REFERBACK_FROM_PENDING_DISBURSEMENT ||
    currentApplicationData?.applicationStatus === ApplicationStatusEnum.FOLLOWED_UP;

  const customerInfo = [
    {
      title: 'Full Name',
      value: currentApplicationData?.applicantFirstName,
    },
    {
      title: 'Identity No.',
      value: currentApplicationData?.applicantNric,
    },
    {
      title: 'Date Of Birth',
      value: currentApplicationData?.applicantNric
        ? getNricInfo(currentApplicationData?.applicantNric ?? '').birthDate.format('DD/MM/YYYY')
        : '-',
    },
    {
      title: 'Age',
      value: currentApplicationData?.applicantNric
        ? getNricInfo(currentApplicationData?.applicantNric ?? '').age
        : '-',
    },
    {
      title: 'Race',
      value: currentApplicationData?.applicantRace,
    },
    {
      title: 'Phone No.',
      value: trimPhoneNo(currentApplicationData?.applicantPhoneNo),
    },
    {
      title: 'Email Address',
      value: currentApplicationData?.applicantEmail,
    },
    {
      title: 'Residential Address',
      value:
        currentApplicationData?.applicantAddress1 != null
          ? [
              currentApplicationData?.applicantAddress1 ??
                currentApplicationData?.user?.residentialAddress1,
              currentApplicationData?.applicantAddress2 ??
                currentApplicationData?.user?.residentialAddress2,
              currentApplicationData?.applicantAddress3 ??
                currentApplicationData?.user?.residentialAddress3,
            ]
              .filter((item) => item != null && item !== '')
              .join(', ')
          : '-',
    },
    {
      title: 'Postcode',
      value: currentApplicationData?.applicantPostcode,
    },
    {
      title: 'City',
      value: currentApplicationData?.applicantCity,
    },
    {
      title: 'State',
      value: currentApplicationData?.applicantState,
    },
    {
      title: 'Partner ID No',
      value: currentApplicationData?.applicantPartnerId,
    },
    {
      title: 'IC Type',
      value: currentApplicationData?.user?.icType ?? '-',
    },
  ];

  const employmentInfo = [
    {
      title: 'Employment Type',
      value: currentApplicationData?.applicantEmploymentType,
    },
    {
      title: 'Monthly Net Salary',
      value:
        currentApplicationData?.actualMonthlySalary != null
          ? `RM ${numberWithCommas(currentApplicationData?.actualMonthlySalary)}`
          : '-',
    },
    {
      title: 'Job Start Date',
      value: currentApplicationData?.employmentStartingFrom
        ? displayDateFromString(currentApplicationData?.employmentStartingFrom)
        : '-',
    },
    {
      title: "Employer's Name",
      value: currentApplicationData?.employerName,
    },
    {
      title: "Employer's Contact No.",
      value:
        currentApplicationData?.employerContactNo != null
          ? currentApplicationData?.employerContactNo
          : '-',
    },
    {
      title: 'Job Title',
      value: currentApplicationData?.jobTitle,
    },
    {
      title: 'Bank Name',
      value: currentApplicationData?.applicantBankName,
    },
    {
      title: 'Bank Account No.',
      value: currentApplicationData?.applicantPersonalBankNo,
    },
  ];

  const emergencyInfo = [
    {
      title: 'First Emergency Contact Persons Name',
      value: currentApplicationData?.emergencyContactName,
    },
    {
      title: 'First Relationship',
      value: currentApplicationData?.applicantEmergencyContactRelationship,
    },
    {
      title: 'First Emergency Contact No.',
      value:
        currentApplicationData?.emergencyContactNo != null
          ? currentApplicationData?.emergencyContactNo
          : '-',
    },
    {
      title: 'Second Emergency Contact Persons Name',
      value: currentApplicationData?.secondEmergencyContactName,
    },
    {
      title: 'Second Relationship',
      value: currentApplicationData?.applicantSecondEmergencyContactRelationship,
    },

    {
      title: 'Second Emergency Contact No.',
      value:
        currentApplicationData?.secondEmergencyContactNo != null
          ? currentApplicationData?.secondEmergencyContactNo
          : '-',
    },
  ];

  async function initProcess() {
    if (!readOnlyMode) {
      dispatch(
        getApplicationDetailsThunk({
          applicationId: applicationDetails?.data?.id,
        })
      );
    }
  }

  return (
    <div className="info-container">
      <InfoGrid
        title="Customer Information"
        data={customerInfo}
        extra={
          !readOnly &&
          !readOnlyMode &&
          editable && (
            <CustomerFormModal
              key={currentApplicationData?.id}
              applicationDetails={currentApplicationData}
              callback={initProcess}
              from="cep"
            />
          )
        }
      />
      <br />
      <br />
      <InfoGrid title="Employment Information" data={employmentInfo} />
      <br />
      <br />
      <InfoGrid title="Emergency Information" data={emergencyInfo} />
    </div>
  );
};

export default ApplicationInfoTab;
